import type { ReactElement } from 'react';
import { Text } from '@mantine/core';
import type { TopicDefinition } from './TopicCatalogTypes';
import type { UserTrendRow } from './SurfaceTopicViewModel';
import RankedList, { type RankedListColumn, type RankedListPrimary } from '@/design/RankedList/RankedList';
import Sparkline from '@/design/Sparkline/Sparkline';
import { sliceToPath } from './TopicSliceKey';
import { formatCount, metricLabel, peakShareFill } from '@/shared/analytics/MetricFormatting';

const METRIC_WIDTH = 72;
const DELTA_WIDTH = 64;
const SPARK_WIDTH = 132;

interface UserTrendListVizProps {
    definition: TopicDefinition;
    rows: UserTrendRow[];
    basePath: string;
}

/**
 * The L1 view for a `user-trend-list` topic: users ranked by how sharply their
 * usage is accelerating (`rising`) or decaying (`declining`), each with a
 * sparkline of their bucketed activity. Rows drill into the person's prompts.
 */
export default function UserTrendListViz({ definition, rows, basePath }: UserTrendListVizProps): ReactElement {
    const declining = definition.trendDirection === 'declining';
    const sparkColor = declining ? 'var(--bm-color-danger)' : 'var(--bm-color-success)';
    const metric = definition.headlineMetric;
    const max = rows.reduce((peak, row) => Math.max(peak, row.current), 0);

    if (rows.length === 0) {
        return (
            <Text size="sm" c="dimmed">
                No users {declining ? 'trending downward' : 'trending upward'} in this window.
            </Text>
        );
    }

    const primary: RankedListPrimary<UserTrendRow> = {
        key: 'label',
        header: 'Person',
        label: (row) => row.label,
        fill: (row) => peakShareFill(row.current, max, 2),
        sort: { get: (row) => row.label, initialDir: 'asc' },
    };

    const columns: RankedListColumn<UserTrendRow>[] = [
        { key: 'current', header: metricLabel(metric), width: METRIC_WIDTH, render: (row) => formatCount(row.current), sort: { get: (row) => row.current, initialDir: 'desc' } },
        {
            key: 'deltaPct',
            header: 'Δ',
            width: DELTA_WIDTH,
            size: 'xs',
            render: (row) => (
                <span style={{ color: row.deltaPct >= 0 ? 'var(--bm-color-success)' : 'var(--bm-color-danger)' }}>
                    {`${row.deltaPct >= 0 ? '+' : ''}${Math.round(row.deltaPct)}%`}
                </span>
            ),
            sort: { get: (row) => row.deltaPct, initialDir: declining ? 'asc' : 'desc' },
        },
        { key: 'trend', header: 'Trend', width: SPARK_WIDTH, raw: true, visibleFrom: 'sm', render: (row) => <Sparkline values={row.series} color={sparkColor} height={22} /> },
    ];

    return (
        <RankedList
            rows={rows}
            primary={primary}
            columns={columns}
            rowKey={(row) => row.userId}
            accent={definition.accent}
            initialSort={{ key: 'deltaPct', dir: declining ? 'asc' : 'desc' }}
            hrefFor={(row) => sliceToPath(basePath, definition.id, { primary: row.userId })}
            unitPlural={definition.lensPlural}
        />
    );
}
